import Constants from "expo-constants"

export type SupabaseOAuthTokens = {
  access_token?: string
  refresh_token?: string
  expires_in?: number
  token_type?: string
  scope?: string
  id_token?: string
}

export type SupabaseOAuthCallbackResult =
  | {
      status: "success"
      tokens: SupabaseOAuthTokens
      returnTo: string
    }
  | {
      status: "denied" | "error"
      message: string
      returnTo: string
    }

export type SupabaseOAuthConfig = {
  supabaseUrl: string
  authorizationEndpoint: string
  tokenEndpoint: string
  tokenExchangeEndpoint: string
  jwksEndpoint: string
  discoveryEndpoint: string
  clientId: string
  redirectUri: string
  scope: string
}

type StoredOAuthRequest = {
  codeVerifier: string
  state: string
  returnTo: string
  redirectUri: string
  createdAt: number
}

declare const process: {
  env: {
    EXPO_PUBLIC_SUPABASE_URL?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_CLIENT_ID?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_REDIRECT_URI?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_SCOPE?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_AUTHORIZATION_ENDPOINT?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_TOKEN_EXCHANGE_ENDPOINT?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_JWKS_ENDPOINT?: string
    EXPO_PUBLIC_SUPABASE_OAUTH_DISCOVERY_ENDPOINT?: string
  }
}

const OAUTH_REQUEST_STORAGE_KEY = "anuka.eq.supabaseOAuth.request"
const DEFAULT_OAUTH_SCOPE = "email profile"
const DEFAULT_AUTH_RETURN_PATH = "/profile"
const DEFAULT_SUPABASE_URL = "https://bgvjsnmnxwicbmvlzjpd.supabase.co"
const DEFAULT_OAUTH_TOKEN_EXCHANGE_ENDPOINT = "/.netlify/functions/oauth-token"

function getProcessEnv(name: string): string | undefined {
  switch (name) {
    case "EXPO_PUBLIC_SUPABASE_URL":
      return process.env.EXPO_PUBLIC_SUPABASE_URL
    case "EXPO_PUBLIC_SUPABASE_OAUTH_CLIENT_ID":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_CLIENT_ID
    case "EXPO_PUBLIC_SUPABASE_OAUTH_REDIRECT_URI":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_REDIRECT_URI
    case "EXPO_PUBLIC_SUPABASE_OAUTH_SCOPE":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_SCOPE
    case "EXPO_PUBLIC_SUPABASE_OAUTH_AUTHORIZATION_ENDPOINT":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_AUTHORIZATION_ENDPOINT
    case "EXPO_PUBLIC_SUPABASE_OAUTH_TOKEN_EXCHANGE_ENDPOINT":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_TOKEN_EXCHANGE_ENDPOINT
    case "EXPO_PUBLIC_SUPABASE_OAUTH_JWKS_ENDPOINT":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_JWKS_ENDPOINT
    case "EXPO_PUBLIC_SUPABASE_OAUTH_DISCOVERY_ENDPOINT":
      return process.env.EXPO_PUBLIC_SUPABASE_OAUTH_DISCOVERY_ENDPOINT
    default:
      return undefined
  }
}

function getEnv(name: string): string | undefined {
  const processValue = getProcessEnv(name)
  if (processValue) return processValue

  const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined
  const extraValue = extra?.[name]
  return typeof extraValue === "string" && extraValue.length > 0 ? extraValue : undefined
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, "")
}

export function isSupabaseOAuthClientConfigured() {
  return Boolean(getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_CLIENT_ID"))
}

export function getSupabaseOAuthAuthorizeEndpoint(supabaseUrl: string) {
  return `${trimTrailingSlash(supabaseUrl)}/auth/v1/oauth/authorize`
}

export function getSupabaseOAuthTokenEndpoint(supabaseUrl: string) {
  return `${trimTrailingSlash(supabaseUrl)}/auth/v1/oauth/token`
}

export function getSupabaseOAuthJwksEndpoint(supabaseUrl: string) {
  return `${trimTrailingSlash(supabaseUrl)}/auth/v1/.well-known/jwks.json`
}

export function getSupabaseOAuthDiscoveryEndpoint(supabaseUrl: string) {
  return `${trimTrailingSlash(supabaseUrl)}/auth/v1/.well-known/openid-configuration`
}

export function getSupabaseOAuthRedirectUri() {
  const configuredRedirectUri = getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_REDIRECT_URI")
  if (configuredRedirectUri) return configuredRedirectUri

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/auth/callback`
  }

  return "emocoach://auth/callback"
}

export function getSupabaseOAuthConfig(): SupabaseOAuthConfig {
  const supabaseUrl = trimTrailingSlash(getEnv("EXPO_PUBLIC_SUPABASE_URL") ?? DEFAULT_SUPABASE_URL)
  const clientId = getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_CLIENT_ID")

  if (!clientId) {
    throw new Error("Supabase OAuth client ID is not configured for EMO.coach sign-in.")
  }

  return {
    supabaseUrl,
    authorizationEndpoint:
      getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_AUTHORIZATION_ENDPOINT") ??
      getSupabaseOAuthAuthorizeEndpoint(supabaseUrl),
    tokenEndpoint: getSupabaseOAuthTokenEndpoint(supabaseUrl),
    tokenExchangeEndpoint:
      getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_TOKEN_EXCHANGE_ENDPOINT") ??
      DEFAULT_OAUTH_TOKEN_EXCHANGE_ENDPOINT,
    jwksEndpoint:
      getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_JWKS_ENDPOINT") ??
      getSupabaseOAuthJwksEndpoint(supabaseUrl),
    discoveryEndpoint:
      getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_DISCOVERY_ENDPOINT") ??
      getSupabaseOAuthDiscoveryEndpoint(supabaseUrl),
    clientId,
    redirectUri: getSupabaseOAuthRedirectUri(),
    scope: getEnv("EXPO_PUBLIC_SUPABASE_OAUTH_SCOPE") ?? DEFAULT_OAUTH_SCOPE,
  }
}

export function getDefaultOAuthReturnPath() {
  return DEFAULT_AUTH_RETURN_PATH
}

export function sanitizeOAuthReturnPath(value: string | null | undefined) {
  if (!value) return getDefaultOAuthReturnPath()

  try {
    const decoded = decodeURIComponent(value)

    if (!decoded.startsWith("/") || decoded.startsWith("//")) {
      return getDefaultOAuthReturnPath()
    }

    const parsed = new URL(decoded, "https://app.anuka.pro")
    if (parsed.origin !== "https://app.anuka.pro") {
      return getDefaultOAuthReturnPath()
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return getDefaultOAuthReturnPath()
  }
}

export function getStoredOAuthRequest(): StoredOAuthRequest | null {
  if (typeof window === "undefined") return null

  const stored = window.sessionStorage.getItem(OAUTH_REQUEST_STORAGE_KEY)
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored) as Partial<StoredOAuthRequest>
    if (
      typeof parsed.codeVerifier !== "string" ||
      typeof parsed.state !== "string" ||
      typeof parsed.returnTo !== "string" ||
      typeof parsed.redirectUri !== "string" ||
      typeof parsed.createdAt !== "number"
    ) {
      return null
    }

    return parsed as StoredOAuthRequest
  } catch {
    return null
  }
}

export function clearStoredOAuthRequest() {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(OAUTH_REQUEST_STORAGE_KEY)
}

function storeOAuthRequest(request: StoredOAuthRequest) {
  window.sessionStorage.setItem(OAUTH_REQUEST_STORAGE_KEY, JSON.stringify(request))
}

function base64UrlEncode(bytes: Uint8Array) {
  let binary = ""
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function randomBase64Url(byteLength: number) {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

export async function createCodeChallenge(codeVerifier: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier))
  return base64UrlEncode(new Uint8Array(digest))
}

export function buildSupabaseOAuthAuthorizeUrl({
  codeChallenge,
  config,
  state,
}: {
  codeChallenge: string
  config: SupabaseOAuthConfig
  state: string
}) {
  const url = new URL(config.authorizationEndpoint)

  url.search = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  }).toString()

  return url.toString()
}

export async function startSupabaseOAuthSignIn(returnTo = getDefaultOAuthReturnPath()) {
  if (typeof window === "undefined") {
    throw new Error("OAuth sign-in is only available in a browser.")
  }

  const config = getSupabaseOAuthConfig()
  const codeVerifier = randomBase64Url(64)
  const state = randomBase64Url(32)
  const codeChallenge = await createCodeChallenge(codeVerifier)

  storeOAuthRequest({
    codeVerifier,
    state,
    returnTo: sanitizeOAuthReturnPath(returnTo),
    redirectUri: config.redirectUri,
    createdAt: Date.now(),
  })

  window.location.assign(buildSupabaseOAuthAuthorizeUrl({ codeChallenge, config, state }))
}

export async function completeSupabaseOAuthCallback(
  currentUrl: string,
  config = getSupabaseOAuthConfig(),
): Promise<SupabaseOAuthCallbackResult> {
  const url = new URL(currentUrl)
  const storedRequest = getStoredOAuthRequest()
  const returnTo = sanitizeOAuthReturnPath(storedRequest?.returnTo)
  const error = url.searchParams.get("error")

  if (error) {
    clearStoredOAuthRequest()
    return {
      status: error === "access_denied" ? "denied" : "error",
      message: url.searchParams.get("error_description") ?? "Authorization was not completed.",
      returnTo,
    }
  }

  const code = url.searchParams.get("code")
  const state = url.searchParams.get("state")

  if (!code) {
    return { status: "error", message: "Missing OAuth authorization code.", returnTo }
  }

  if (!storedRequest) {
    return {
      status: "error",
      message: "Missing OAuth sign-in request. Please try again.",
      returnTo,
    }
  }

  if (!state || state !== storedRequest.state) {
    clearStoredOAuthRequest()
    return { status: "error", message: "OAuth state did not match. Please try again.", returnTo }
  }

  const response = await fetch(config.tokenExchangeEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      client_id: config.clientId,
      redirect_uri: storedRequest.redirectUri,
      code_verifier: storedRequest.codeVerifier,
    }),
  })

  const responseText = await response.text()
  let body: Record<string, unknown> = {}

  try {
    body = responseText ? (JSON.parse(responseText) as Record<string, unknown>) : {}
  } catch {
    body = {}
  }

  if (!response.ok) {
    clearStoredOAuthRequest()
    return {
      status: "error",
      message:
        typeof body.error_description === "string"
          ? body.error_description
          : typeof body.error === "string"
            ? body.error
            : responseText || "Could not finish OAuth sign-in.",
      returnTo,
    }
  }

  clearStoredOAuthRequest()

  return {
    status: "success",
    tokens: body as SupabaseOAuthTokens,
    returnTo,
  }
}
